/* eslint-disable react-hooks/exhaustive-deps */
import { CubeTransparentIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { NewspaperIcon, PresentationChartBarIcon } from "@heroicons/react/24/outline";
import { Type, Sparkles } from "lucide-react";
import React, { useMemo, useState } from "react";
import HubspotIcon from "../../../assets/images/hubspot.svg?react";
import He from "../../../assets/images/heSm.svg?react";
import Segment from "../../../assets/images/segmentAvatar.svg?react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import cn from "../../../utils/styleHelpers";

type PopoverPosition =
  | "bottom"
  | "left"
  | "right"
  | "top"
  | "bottom-end"
  | "bottom-start"
  | "left-end"
  | "left-start"
  | "right-end"
  | "right-start"
  | "top-end"
  | "top-start";

type Props = {
  list?: any[];
  successMetricsList?: any[];
  metricsList?: any[];
  selected: any;
  setSelected: any;
  type?: string;
  children?: React.ReactNode;
  isOpen: any;
  setIsOpen: any;
  position?: PopoverPosition;
};

type SourceFilter = {
  name: string;
  label: string;
  icon: React.ElementType;
};

type AttributeFilter = {
  name: "all" | "traits" | "success_metrics" | "metrics";
  label: string;
  icon: React.ElementType;
};

const listOfSources: SourceFilter[] = [
  {
    name: "all",
    label: "All Sources",
    icon: CubeTransparentIcon,
  },
  {
    name: "native",
    label: "Hyperengage",
    icon: He,
  },
  {
    name: "segment",
    label: "Segment",
    icon: Segment,
  },
  {
    name: "hubspot",
    label: "HubSpot",
    icon: HubspotIcon,
  },
];

const listOfAttributes: AttributeFilter[] = [
  {
    name: "all",
    label: "All",
    icon: CubeTransparentIcon,
  },
  {
    name: "traits",
    label: "Traits",
    icon: NewspaperIcon,
  },
  {
    name: "success_metrics",
    label: "Success Metrics",
    icon: Sparkles,
  },
  {
    name: "metrics",
    label: "Metrics",
    icon: PresentationChartBarIcon,
  },
];

const getPopoverPlacement = (position: PopoverPosition) => {
  const [sideRaw, alignRaw] = position.split("-") as [string, string | undefined];
  const side = (["top", "bottom", "left", "right"].includes(sideRaw) ? sideRaw : "bottom") as "top" | "bottom" | "left" | "right";
  const align = (alignRaw === "start" || alignRaw === "end" ? alignRaw : "start") as "start" | "center" | "end";
  return { side, align };
};

const sourcePillClasses: Record<string, string> = {
  native: "bg-primary/10 text-primary border-primary/20",
  segment: "bg-blue-50 text-blue-700 border-blue-200",
  hubspot: "bg-orange-50 text-orange-700 border-orange-200",
};

const MultiSelectDropdown = ({
  list = [],
  successMetricsList = [],
  metricsList = [],
  isOpen = false,
  setIsOpen,
  children,
  type = "Accounts",
  selected,
  setSelected,
  position = "left-start",
}: Props) => {
  const [query, setQuery] = useState<string>("");
  const [selectedSource, setSelectedSource] = useState<string>("all");
  const [selectedAttribute, setSelectedAttribute] = useState<"all" | "traits" | "success_metrics" | "metrics">("all");

  const selectedNames = useMemo<string[]>(
    () => (Array.isArray(selected) ? selected : []),
    [selected],
  );

  const displayTraits = useMemo(
    () => (
      (selectedSource === "all"
        ? list
        : list.filter((item: any) => item?.source === selectedSource))
        .filter((item: any) => item?.name !== "accounts.name" && item?.name !== "users.name")
    ),
    [selectedSource, list],
  );

  const displaySuccessMetrics = useMemo(
    () => (
      selectedSource === "all"
        ? successMetricsList
        : successMetricsList.filter((item: any) => item?.source === selectedSource)
    ),
    [selectedSource, successMetricsList],
  );

  const displayMetrics = useMemo(
    () => (
      selectedSource === "all"
        ? metricsList
        : metricsList.filter((item: any) => item?.source === selectedSource)
    ),
    [selectedSource, metricsList],
  );

  const queriedData = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const buckets = {
      traits: displayTraits,
      success_metrics: displaySuccessMetrics,
      metrics: displayMetrics,
      all: [...displayTraits, ...displaySuccessMetrics, ...displayMetrics],
    };

    const pool = buckets[selectedAttribute] || [];
    const filtered = normalizedQuery
      ? pool.filter((item: any) => item?.display_name?.toLowerCase().includes(normalizedQuery))
      : pool;

    return [...filtered].sort((a: any, b: any) => {
      const aSelected = selectedNames.includes(a?.name);
      const bSelected = selectedNames.includes(b?.name);
      if (aSelected !== bSelected) return aSelected ? -1 : 1;
      return String(a?.display_name || "").localeCompare(String(b?.display_name || ""));
    });
  }, [query, selectedAttribute, displayTraits, displaySuccessMetrics, displayMetrics, selectedNames]);

  const selectedCount = useMemo(
    () => queriedData.filter((item: any) => selectedNames.includes(item?.name)).length,
    [queriedData, selectedNames],
  );

  const { side, align } = getPopoverPlacement(position);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="inline-flex">{children}</div>
      </PopoverTrigger>
      <PopoverContent side={side} align={align} className="w-[360px] p-0 z-[100]">
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="border-b border-border p-3">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder={`Search ${type} columns`}
                onKeyDown={(event) => event.stopPropagation()}
                className="h-8 pl-8"
              />
            </div>
          </div>

          <div className="space-y-3 border-b border-border bg-muted/40 p-3">
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Source</div>
              <div className="flex flex-wrap gap-1.5">
                {listOfSources.map((source) => {
                  const SourceIcon = source.icon;
                  const active = selectedSource === source.name;
                  return (
                    <button
                      key={source.name}
                      type="button"
                      onClick={() => {
                        setSelectedSource(source.name);
                        if (selectedAttribute !== "all") setSelectedAttribute("all");
                      }}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                        active
                          ? sourcePillClasses[source.name] || "bg-primary/10 text-primary border-primary/20"
                          : "border-border bg-card text-muted-foreground hover:bg-muted",
                      )}
                    >
                      <SourceIcon className="h-3.5 w-3.5" />
                      <span>{source.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Type</div>
              <div className="inline-flex flex-wrap rounded-md border border-border bg-card p-0.5 gap-0.5">
                {listOfAttributes
                  .filter((item) => {
                    if (item.name === "success_metrics") return successMetricsList.length > 0;
                    if (item.name === "metrics") return metricsList.length > 0;
                    return true;
                  })
                  .map((item) => {
                  const Icon = item.icon;
                  const active = selectedAttribute === item.name;
                  return (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => setSelectedAttribute(item.name)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors whitespace-nowrap",
                        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
                      )}
                    >
                      <Icon className="h-3 w-3 flex-shrink-0" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs text-muted-foreground">
            <span>{selectedCount} selected in this view</span>
            <span>{queriedData.length} results</span>
          </div>

          <div className="max-h-[300px] overflow-y-auto">
            {queriedData.length ? queriedData.map((item: any) => {
              const isChecked = selectedNames.includes(item.name);
              const isComputed = item?.generation_type === "computed_trait" || item?.attribute_type === "computed_traits";
              const isMetric = !isComputed && (item?.attribute_type === "metric_traits" || item?.attribute_type === "metrics");

              return (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => setSelected(item)}
                  className={cn(
                    "flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left transition-colors last:border-0",
                    isChecked ? "bg-primary/5" : "hover:bg-muted/70",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    readOnly
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelected(item);
                    }}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{item.display_name}</div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className={cn(
                        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium",
                        isComputed
                          ? "bg-violet-50 text-violet-700"
                          : isMetric
                          ? "bg-amber-50 text-amber-700"
                          : "bg-emerald-50 text-emerald-700",
                      )}
                      >
                        {isComputed ? <Sparkles className="h-3 w-3" /> : isMetric ? <PresentationChartBarIcon className="h-3 w-3" /> : <Type className="h-3 w-3" />}
                        {isComputed ? "Computed" : isMetric ? "Metric" : "Trait"}
                      </span>
                      {item?.source && item.source !== "all" && (
                        <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {item.source}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            }) : (
              <div className="flex h-[180px] flex-col items-center justify-center gap-2 px-4 text-center">
                <CubeTransparentIcon className="h-5 w-5 text-muted-foreground/70" />
                <p className="text-sm font-medium text-muted-foreground">No matching columns</p>
                <p className="text-xs text-muted-foreground/80">Try a different search or filter.</p>
              </div>
            )}
          </div>

          <div className="flex justify-end border-t border-border p-2">
            <Button size="sm" variant="outline" onClick={() => setIsOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelectDropdown;
